import { useEffect, useMemo, useState } from 'react';
import { batchLookupAdminUsers } from '@/services/auth/api';

const cache = new Map<string, AuthAPI.AdminUser>();
const negative = new Set<string>();
const pending = new Map<string, Promise<void>>();

const listeners = new Set<() => void>();
function notify() {
  for (const l of listeners) l();
}

const HEX_24 = /^[0-9a-f]{24}$/i;
const BATCH_MAX = 200;

async function lookupChunk(ids: string[]) {
  try {
    const r = await batchLookupAdminUsers(ids);
    const found = new Set<string>();
    if (r?.success && r.data?.users) {
      for (const u of r.data.users) {
        if (u._id) {
          cache.set(u._id, u);
          found.add(u._id);
        }
      }
    }

    for (const id of ids) {
      if (!found.has(id)) negative.add(id);
    }
  } catch {
    for (const id of ids) negative.add(id);
  }
  notify();
}

export function useUserMap(userIds: string[] | undefined): {
  userMap: Record<string, AuthAPI.AdminUser>;
  isUserLookupSettled: (userId?: string | null) => boolean;
} {
  const [, setTick] = useState(0);

  const idsKey = useMemo(() => {
    if (!userIds || userIds.length === 0) return '';
    const set = new Set<string>();
    for (const id of userIds) {
      if (id && HEX_24.test(id)) set.add(id);
    }
    if (set.size === 0) return '';
    return Array.from(set).sort().join('|');
  }, [userIds]);

  useEffect(() => {
    const sub = () => setTick((t) => t + 1);
    listeners.add(sub);
    return () => {
      listeners.delete(sub);
    };
  }, []);

  useEffect(() => {
    if (!idsKey) return;
    const ids = idsKey.split('|');
    const missing = ids.filter(
      (id) => !cache.has(id) && !negative.has(id) && !pending.has(id),
    );
    if (missing.length === 0) return;

    for (let i = 0; i < missing.length; i += BATCH_MAX) {
      const chunk = missing.slice(i, i + BATCH_MAX);
      const p = lookupChunk(chunk).finally(() => {
        for (const id of chunk) pending.delete(id);
      });
      for (const id of chunk) pending.set(id, p);
    }
  }, [idsKey]);

  const userMap: Record<string, AuthAPI.AdminUser> = {};
  if (idsKey) {
    for (const id of idsKey.split('|')) {
      const u = cache.get(id);
      if (u) userMap[id] = u;
    }
  }
  return {
    userMap,
    isUserLookupSettled: (userId?: string | null) => {
      if (!userId || !HEX_24.test(userId)) return true;
      return cache.has(userId) || negative.has(userId);
    },
  };
}
